/**
 * Eine Liste von typisierten Intervallen.
 * Der Typ ist eine Zahl. Intervalle höheren Typs liegen über Intervallen niedrigeren Typs.
 */
import * as ClosedInterval from "./ClosedInterval";
import { Interval } from "./ClosedInterval";

type PriorityInterval = [number, number, number, ...any[]];

export class PriorityIntervals {
  /** @type {number[][]} */
  list: PriorityInterval[] = [];

  /**
   * @param {Array<Array<number>>}
   */
  constructor(intervals: PriorityInterval[] = []) {
    this.add(Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY);
    intervals.forEach((i) => this.add(i[0], i[1], i[2], ...i.slice(3)));
  }

  /**
   * fügt ein Intervall ein
   * @param {number} from
   * @param {number} to
   * @param {number} type
   * @param ...data optionale zusätzliche Daten, die mitgeführt werden können, aber vom PriorityInterval ignoriert werden
   * @returns {PriorityIntervals}
   */
  add(from: number, to: number, type: number, ...data: any[]): this {
    this.list.push([Math.min(from, to), Math.max(from, to), type, ...data]);
    return this;
  }

  /**
   * sortiert und klopft alle Intervalle flach - höherwertige verdecken niederwertige.
   * MinLength gibt die Mindestlänge der erzeugten Intervalle an.
   * Partition löscht eventuelle mitgeführte Daten (...data)
   * @param {number} minLength
   * @returns {PriorityIntervals}
   */
  partition(minLength: number = 0): this {
    const partitions: PriorityInterval[] = [];
    let current: PriorityInterval | null = null;
    this.getSortedPoints().reduce((l, r) => {
      const t = this.list
        .filter((i) => i[0] <= l && r <= i[1])
        .reduce((max, i) => Math.max(max, i[2]), Number.NEGATIVE_INFINITY);

      if (r - l >= minLength) {
        if (current === null || current[2] !== t) {
          current = [l, r, t];
          partitions.push(current);
        } else {
          current[1] = r;
        }
      }

      return r;
    });
    this.list = partitions;
    return this;
  }

  /**
   * Gibt eine sortierte Liste aller Intervallgrenzen zurück
   * @returns {number[]}
   */
  getSortedPoints(): number[] {
    const points = new Set<number>();
    for (let i of this.list) {
      points.add(i[0]);
      points.add(i[1]);
    }
    return Array.from(points).sort((a, b) => a - b);
  }

  /**
   * verschiebt die Intervalle so, dass das erste Intervall auf 0 beginnt
   * @returns {PriorityIntervals}
   */
  normalize(): this {
    this.finite();
    this.translate(-this.left);
    return this;
  }

  /**
   * addiert delta auf alle Intervallgrenzen
   * @param delta
   * @returns {PriorityIntervals}
   */
  translate(delta: number): this {
    this.list.forEach((i) => {
      i[0] += delta;
      i[1] += delta;
    });
    return this;
  }

  /**
   * entfernt alle Intervalle des Typs oo
   * @returns {PriorityIntervals}
   */
  finite(): this {
    this.list = this.list.filter((i) => Number.isFinite(i[2]));
    return this;
  }

  /**
   * Linkeste Intervallgrenze
   */
  get left(): number {
    return this.list.reduce(
      (min, i) => Math.min(min, Math.min(i[0], i[1])),
      Number.POSITIVE_INFINITY,
    );
  }

  /**
   * Rechteste Intervallgrenze
   */
  get right(): number {
    return this.list.reduce(
      (max, i) => Math.max(max, Math.max(i[0], i[1])),
      Number.NEGATIVE_INFINITY,
    );
  }

  /**
   * size
   * @returns {number}
   */
  get size(): number {
    return this.right - this.left;
  }

  /**
   * letzter Index
   * @returns {number}
   */
  get lastIndex(): number {
    return this.list.length - 1;
  }

  /**
   * erzeugt ein Interval ausgehend vom Start-Interval-Index rückwärts, solange condition true ist.
   * @returns {?number[]}
   */
  backwardInterval(
    intervalIndex: number,
    condition: (i: PriorityInterval) => boolean,
  ): Interval | null {
    if (intervalIndex < 0 || this.list.length <= intervalIndex) {
      return null;
    }
    const right = this.list[intervalIndex][1];
    let left: number | null = null;
    for (; intervalIndex >= 0 && condition(this.list[intervalIndex]); intervalIndex--) {
      left = this.list[intervalIndex][0];
    }
    if (left === null) {
      return null;
    }
    return [left, right];
  }

  /**
   * erzeugt ein Interval ausgehend vom Start-Interval-Index vorwärts, solange condition true ist
   * @returns {?number[]}
   */
  forwardInterval(
    intervalIndex: number,
    condition: (i: PriorityInterval) => boolean,
  ): Interval | null {
    if (intervalIndex < 0 || this.list.length <= intervalIndex) {
      return null;
    }
    const left = this.list[intervalIndex][0];
    let right: number | null = null;
    for (
      ;
      intervalIndex < this.list.length && condition(this.list[intervalIndex]);
      intervalIndex++
    ) {
      right = this.list[intervalIndex][1];
    }
    if (right === null) {
      return null;
    }
    return [left, right];
  }

  /**
   * gibt den Typ mit der Höchsten Prio im gegebenen Interval zurück
   * @param {any}
   */
  getTypeIn(interval: Interval): number {
    return this.list
      .filter((i) => ClosedInterval.intersects([i[0], i[1]], interval))
      .reduce((max, i) => Math.max(max, i[2]), Number.NEGATIVE_INFINITY);
  }

  /**
   * gibt die Lönge eines Intervalls zurück
   * @param {number[]} interval
   * @returns {number|number}
   */
  static intervalSize(interval: Interval | null): number {
    return interval == null ? 0 : interval[1] - interval[0];
  }
}
